import { FrequencyEditor } from './FrequencyEditor';
import { TagsComponent } from './TagsComponent';
import type {
  ScheduledFrequency,
  ScheduledReminder,
} from '@/data/scheduledReminders';
import { $defaultFrequency } from '@/stores';
import clsx from 'clsx';

export type ScheduledReminderFormData = Omit<
  ScheduledReminder,
  'id' | 'lastRun'
>;

interface ScheduledReminderFormProps {
  value: ScheduledReminderFormData;
  onChange: (value: ScheduledReminderFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  title?: string;
  submitLabel?: string;
}

export function ScheduledReminderForm({
  value,
  onChange,
  onSave,
  onCancel,
  title = 'New Scheduled Reminder',
  submitLabel = 'Save',
}: ScheduledReminderFormProps) {
  return (
    <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-4">
      <h3 className="mb-3 font-semibold text-gray-900">{title}</h3>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium">Reminder Text</label>
          <input
            type="text"
            value={value.template.reminder}
            onChange={e =>
              onChange({
                ...value,
                template: {
                  ...value.template,
                  reminder: e.target.value,
                },
              })
            }
            className="w-full rounded border border-gray-300 px-3 py-2"
            placeholder="e.g. Radio check with range control"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium">Call Sign From</label>
            <input
              type="text"
              value={value.template.callSign.from}
              onChange={e =>
                onChange({
                  ...value,
                  template: {
                    ...value.template,
                    callSign: {
                      ...value.template.callSign,
                      from: e.target.value.toUpperCase(),
                    },
                  },
                })
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
              placeholder="From"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Call Sign To</label>
            <input
              type="text"
              value={value.template.callSign.to}
              onChange={e =>
                onChange({
                  ...value,
                  template: {
                    ...value.template,
                    callSign: {
                      ...value.template.callSign,
                      to: e.target.value.toUpperCase(),
                    },
                  },
                })
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
              placeholder="To"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium">Description</label>
          <input
            type="text"
            value={value.template.description ?? ''}
            onChange={e =>
              onChange({
                ...value,
                template: {
                  ...value.template,
                  description: e.target.value,
                },
              })
            }
            className="w-full rounded border border-gray-300 px-3 py-2"
            placeholder="Will be set as the description when added to the log"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Operating Frequency
          </label>
          <input
            type="text"
            value={value.template.frequency ?? ''}
            onChange={e =>
              onChange({
                ...value,
                template: {
                  ...value.template,
                  frequency: e.target.value,
                },
              })
            }
            className="w-full rounded border border-gray-300 px-3 py-2"
            placeholder={$defaultFrequency.get()}
          />
        </div>

        <div>
          <label className="block text-sm font-medium">Tags</label>
          <div className="w-fit">
            <TagsComponent
              left
              tagIds={value.template.tags ?? []}
              addTag={tag => {
                onChange({
                  ...value,
                  template: {
                    ...value.template,
                    tags: [...(value.template.tags ?? []), tag.id],
                  },
                });
              }}
              removeTag={tagId => {
                onChange({
                  ...value,
                  template: {
                    ...value.template,
                    tags: (value.template.tags ?? []).filter(
                      id => id !== tagId,
                    ),
                  },
                });
              }}
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium">
            Frequency Type
          </label>
          <div className="flex gap-2">
            {['interval', 'hourly', 'daily', 'weekly'].map(kind => (
              <button
                key={kind}
                onClick={() => {
                  let freq: ScheduledFrequency;
                  if (kind === 'interval') {
                    freq = {
                      kind: 'interval',
                      every: { hours: 1 },
                    };
                  } else if (kind === 'hourly') {
                    freq = { kind: 'hourly', minutes: [0] };
                  } else if (kind === 'daily') {
                    freq = {
                      kind: 'daily',
                      times: [{ hour: 0, minute: 0 }],
                    };
                  } else {
                    freq = {
                      kind: 'weekly',
                      days: [0],
                      times: [{ hour: 0, minute: 0 }],
                    };
                  }
                  onChange({
                    ...value,
                    frequency: freq,
                  });
                }}
                className={clsx(
                  'rounded px-3 py-1 text-sm',
                  value.frequency.kind === kind
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 hover:bg-gray-300',
                )}
              >
                {kind.charAt(0).toUpperCase() + kind.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded bg-gray-100 p-3">
          <FrequencyEditor
            frequency={value.frequency}
            onChange={freq =>
              onChange({
                ...value,
                frequency: freq,
              })
            }
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={onSave}
            className="flex-1 rounded bg-blue-500 px-3 py-2 text-white hover:bg-blue-600"
          >
            {submitLabel}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 rounded bg-gray-300 px-3 py-2 hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
